import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  FaProjectDiagram,
  FaTimes,
  FaRedoAlt,
  FaLayerGroup,
  FaEye,
  FaInfoCircle,
} from 'react-icons/fa';
import FlowSankeyCard from '../charts/FlowSankeyCard';

const MAX_SELECTED_FLOWS = 5;
const DEFAULT_SELECTED_COUNT = 3;


export default function FlowAdoptionTab({
  flowAdoption = {},
  flows = [],
  loading = false,
  filters = {}
}) {
  const organizationId = filters.organizationId || '';
  const siteCollectionId = filters.siteCollectionId || '';
  const fromUtc = filters.fromUtc || '';
  const toUtc = filters.toUtc || '';

  // Build engagement map from feature adoption data to rank flows
  const engagementMap = useMemo(() => {
    const map = new Map();
    const rawData = flowAdoption?.data;
    if (Array.isArray(rawData)) {
      rawData.forEach((item) => {
        const id = String(item.flowId || item.featureKey || '');
        const count = Number(item.activeUsers ?? item.uniqueUsers ?? item.userCount ?? item.engagement ?? 0);
        if (id) {
          map.set(id, count);
        }
      });
    }
    return map;
  }, [flowAdoption?.data]);

  // Rank flows: highest engagement first, then alphabetical fallback
  const rankedFlows = useMemo(() => {
    if (!Array.isArray(flows) || flows.length === 0) return [];
    return [...flows].sort((a, b) => {
      const countA = engagementMap.get(String(a.id)) || 0;
      const countB = engagementMap.get(String(b.id)) || 0;
      if (countB !== countA) return countB - countA;
      const nameA = a.flowName || a.name || '';
      const nameB = b.flowName || b.name || '';
      return nameA.localeCompare(nameB);
    });
  }, [flows, engagementMap]);

  // Flow lookup map for quick label retrieval
  const flowMap = useMemo(() => {
    const map = new Map();
    (flows || []).forEach((f) => {
      map.set(String(f.id), f.flowName || f.name || `Flow ${f.id}`);
    });
    return map;
  }, [flows]);

  // Selected flow IDs (up to 5)
  const lastSiteRef = useRef(siteCollectionId);
  const initializedSiteRef = useRef(
    rankedFlows.length > 0 ? siteCollectionId : null
  );
  const manuallyAddedFlowIdsRef = useRef(new Set());
  const triedFlowIdsRef = useRef(new Set());

  const [selectedFlowIds, setSelectedFlowIds] = useState(() => {
    if (rankedFlows.length > 0) {
      return rankedFlows.slice(0, DEFAULT_SELECTED_COUNT).map((f) => String(f.id));
    }
    return [];
  });
  const [viewMode, setViewMode] = useState('all'); // 'all' | 'focused'
  const [focusedFlowId, setFocusedFlowId] = useState(() => {
    return rankedFlows[0] ? String(rankedFlows[0].id) : null;
  });

  // Automatically remove empty flow cards that have zero journey telemetry unless manually added
  const handleFlowEmpty = useCallback((emptyFlowId) => {
    if (manuallyAddedFlowIdsRef.current.has(emptyFlowId)) {
      return;
    }

    triedFlowIdsRef.current.add(emptyFlowId);

    setSelectedFlowIds((prev) => {
      if (!prev.includes(emptyFlowId)) return prev;

      // Keep at least one card if all flows in the site are empty
      if (prev.length <= 1) {
        return prev;
      }

      const remaining = prev.filter((id) => id !== emptyFlowId);

      // Check if there is another untried flow in rankedFlows to fill the slot
      const untriedFlow = (rankedFlows || []).find(
        (f) => !remaining.includes(String(f.id)) && !triedFlowIdsRef.current.has(String(f.id))
      );

      if (untriedFlow && remaining.length < DEFAULT_SELECTED_COUNT) {
        return [...remaining, String(untriedFlow.id)];
      }

      return remaining;
    });

    setFocusedFlowId((prev) => {
      if (prev === emptyFlowId) {
        return null;
      }
      return prev;
    });
  }, [rankedFlows]);

  // Keep selection synchronized when siteCollectionId changes or asynchronous flows arrive
  useEffect(() => {
    const isNewSite = lastSiteRef.current !== siteCollectionId;

    if (isNewSite) {
      lastSiteRef.current = siteCollectionId;
      initializedSiteRef.current = rankedFlows.length > 0 ? siteCollectionId : null;
      manuallyAddedFlowIdsRef.current.clear();
      triedFlowIdsRef.current.clear();
      const topIds = rankedFlows.slice(0, DEFAULT_SELECTED_COUNT).map((f) => String(f.id));
      setSelectedFlowIds(topIds);
      setFocusedFlowId(topIds[0] || null);
      return;
    }

    // Site is unchanged: if this site hasn't been initialized yet and flows just loaded
    if (initializedSiteRef.current !== siteCollectionId && rankedFlows.length > 0) {
      initializedSiteRef.current = siteCollectionId;
      const topIds = rankedFlows.slice(0, DEFAULT_SELECTED_COUNT).map((f) => String(f.id));
      setSelectedFlowIds(topIds);
      setFocusedFlowId(topIds[0] || null);
      return;
    }

    // Site is unchanged and already initialized: prune any stale flow IDs that don't belong to current site
    if (selectedFlowIds.length > 0) {
      const validSelectedIds = selectedFlowIds.filter((id) => flowMap.has(id));
      if (validSelectedIds.length !== selectedFlowIds.length) {
        setSelectedFlowIds(validSelectedIds);
        if (!validSelectedIds.includes(focusedFlowId)) {
          setFocusedFlowId(validSelectedIds[0] || null);
        }
      }
    }
  }, [siteCollectionId, rankedFlows, flowMap, selectedFlowIds, focusedFlowId]);

  // Available flows that are not yet selected
  const unselectedFlows = useMemo(() => {
    const selectedSet = new Set(selectedFlowIds);
    return rankedFlows.filter((f) => !selectedSet.has(String(f.id)));
  }, [rankedFlows, selectedFlowIds]);

  // Handlers
  const handleAddFlow = useCallback((e) => {
    const newId = e.target.value;
    if (!newId) return;
    if (selectedFlowIds.length >= MAX_SELECTED_FLOWS) return;

    manuallyAddedFlowIdsRef.current.add(newId);

    setSelectedFlowIds((prev) => {
      if (prev.includes(newId)) return prev;
      const updated = [...prev, newId];
      return updated;
    });

    // Reset dropdown value
    e.target.value = '';
  }, [selectedFlowIds.length]);

  const handleRemoveFlow = useCallback((idToRemove) => {
    setSelectedFlowIds((prev) => {
      const updated = prev.filter((id) => id !== idToRemove);
      if (focusedFlowId === idToRemove) {
        setFocusedFlowId(updated[0] || null);
      }
      return updated;
    });
  }, [focusedFlowId]);

  const handleResetTop3 = useCallback(() => {
    manuallyAddedFlowIdsRef.current.clear();
    triedFlowIdsRef.current.clear();
    const topIds = rankedFlows.slice(0, DEFAULT_SELECTED_COUNT).map((f) => String(f.id));
    setSelectedFlowIds(topIds);
    setFocusedFlowId(topIds[0] || null);
  }, [rankedFlows]);

  const isMaxReached = selectedFlowIds.length >= MAX_SELECTED_FLOWS;

  // Render empty state if no flows exist in the current site collection
  if (!loading && Array.isArray(flows) && flows.length === 0) {
    return (
      <div className="tab-content-pane flow-adoption-tab" data-testid="flow-adoption-tab">
        <div className="flow-adoption-empty-notice" data-testid="flow-adoption-no-flows">
          <FaProjectDiagram className="empty-notice-icon" />
          <h3>No Flows Found</h3>
          <p>
            There are no guided walkthroughs registered in this site collection.
            Publish flows from Content Studio to start viewing user funnel telemetry.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="tab-content-pane flow-adoption-tab" data-testid="flow-adoption-tab">
      {/* Top Banner / Toolbar */}
      <section className="flow-selection-toolbar" aria-label="Flow Comparison Controls">
        <div className="toolbar-top-row">
          <div className="toolbar-title-group">
            <h2 className="toolbar-heading">Flow Journey Funnel Telemetry</h2>
            <p className="toolbar-subtext">
              Track multi-step completion funnels, user transition drop-offs, and journey friction points.
            </p>
          </div>

          <div className="toolbar-controls-right">
            {/* View Mode Toggle */}
            <div className="view-mode-toggle-group" role="group" aria-label="Flow View Mode">
              <button
                type="button"
                className={`view-mode-btn ${viewMode === 'all' ? 'active' : ''}`}
                onClick={() => setViewMode('all')}
                title="View funnels for all selected flows stacked vertically"
                data-testid="view-mode-all-btn"
              >
                <FaLayerGroup /> <span>Stack All ({selectedFlowIds.length})</span>
              </button>
              <button
                type="button"
                className={`view-mode-btn ${viewMode === 'focused' ? 'active' : ''}`}
                onClick={() => setViewMode('focused')}
                title="Focus on one flow with quick-switching tabs"
                data-testid="view-mode-focused-btn"
              >
                <FaEye /> <span>Single Focus</span>
              </button>
            </div>

            {/* Reset to Top 3 */}
            {rankedFlows.length > 0 && (
              <button
                type="button"
                className="btn-toolbar-action reset-top3-btn"
                onClick={handleResetTop3}
                title="Reset selection to Top 3 most active flows"
                data-testid="reset-top3-btn"
              >
                <FaRedoAlt className="action-icon" /> Top 3
              </button>
            )}
          </div>
        </div>

        {/* Selected Flows Chips & Add Selector */}
        <div className="flow-chips-row">
          <div className="flow-chips-list" role="list" aria-label="Selected flows for comparison">
            <span className="chips-label">
              Comparing <strong>{selectedFlowIds.length}</strong> of max <strong>{MAX_SELECTED_FLOWS}</strong>:
            </span>

            {selectedFlowIds.map((id, index) => {
              const name = flowMap.get(id) || `Flow ${id}`;
              const isFocused = viewMode === 'focused' && focusedFlowId === id;

              return (
                <div
                  key={id}
                  role="listitem"
                  className={`flow-selection-chip ${isFocused ? 'chip-focused' : ''}`}
                  data-testid={`selected-flow-chip-${id}`}
                  onClick={() => {
                    if (viewMode === 'focused') {
                      setFocusedFlowId(id);
                    }
                  }}
                >
                  <span className="chip-rank-badge">#{index + 1}</span>
                  <span className="chip-flow-name" title={name}>
                    {name}
                  </span>
                  <button
                    type="button"
                    className="chip-remove-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveFlow(id);
                    }}
                    title={`Remove ${name} from comparison`}
                    aria-label={`Remove flow ${name}`}
                    data-testid={`chip-remove-btn-${id}`}
                  >
                    <FaTimes />
                  </button>
                </div>
              );
            })}

            {/* Add Flow Dropdown */}
            {unselectedFlows.length > 0 && (
              <div className="add-flow-dropdown-wrap">
                <select
                  className={`add-flow-select ${isMaxReached ? 'disabled-max' : ''}`}
                  onChange={handleAddFlow}
                  defaultValue=""
                  disabled={isMaxReached}
                  aria-label="Add flow to comparison"
                  data-testid="add-flow-select"
                >
                  <option value="" disabled>
                    {isMaxReached ? 'Max 5 flows reached' : '+ Add Flow to Compare...'}
                  </option>
                  {unselectedFlows.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.flowName || f.name || f.id}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Focused mode sub-tabs */}
        {viewMode === 'focused' && selectedFlowIds.length > 0 && (
          <nav className="focused-tabs-bar" role="tablist" aria-label="Focused Flow Selector">
            {selectedFlowIds.map((id) => {
              const isActive = focusedFlowId === id;
              const name = flowMap.get(id) || `Flow ${id}`;
              return (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`focused-tab-btn ${isActive ? 'active' : ''}`}
                  onClick={() => setFocusedFlowId(id)}
                  data-testid={`focused-tab-${id}`}
                >
                  <span className="tab-dot" />
                  <span className="tab-name">{name}</span>
                </button>
              );
            })}
          </nav>
        )}
      </section>

      {/* Main Flow Cards Area */}
      <main className="flow-cards-container" data-testid="flow-cards-container">
        {selectedFlowIds.length === 0 ? (
          <div className="no-flows-selected-card" data-testid="no-flows-selected-card">
            <FaInfoCircle className="info-icon" />
            <h3>No Flows Selected</h3>
            <p>Select up to 5 flows from the dropdown above to analyze step funnels and drop-offs.</p>
            {rankedFlows.length > 0 && (
              <button
                type="button"
                className="btn-select-top3"
                onClick={handleResetTop3}
                data-testid="empty-select-top3-btn"
              >
                <FaRedoAlt /> Load Top 3 Flows
              </button>
            )}
          </div>
        ) : viewMode === 'focused' ? (
          focusedFlowId && (
            <FlowSankeyCard
              key={focusedFlowId}
              organizationId={organizationId}
              siteCollectionId={siteCollectionId}
              flowId={focusedFlowId}
              flowName={flowMap.get(focusedFlowId)}
              fromUtc={fromUtc}
              toUtc={toUtc}
              onRemove={handleRemoveFlow}
              onEmpty={handleFlowEmpty}
            />
          )
        ) : (
          selectedFlowIds.map((flowId) => (
            <FlowSankeyCard
              key={flowId}
              organizationId={organizationId}
              siteCollectionId={siteCollectionId}
              flowId={flowId}
              flowName={flowMap.get(flowId)}
              fromUtc={fromUtc}
              toUtc={toUtc}
              onRemove={handleRemoveFlow}
              onEmpty={handleFlowEmpty}
            />
          ))
        )}
      </main>
    </div>
  );
}

FlowAdoptionTab.propTypes = {
  flowAdoption: PropTypes.shape({
    data: PropTypes.array,
    isEntitled: PropTypes.bool,
    error: PropTypes.string
  }),
  flows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      flowName: PropTypes.string,
      name: PropTypes.string
    })
  ),
  loading: PropTypes.bool,
  filters: PropTypes.shape({
    organizationId: PropTypes.string,
    siteCollectionId: PropTypes.string,
    fromUtc: PropTypes.string,
    toUtc: PropTypes.string
  })
};
